import React, { useRef } from 'react'
import clsx from 'clsx'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import ButtonBase from '@mui/material/ButtonBase'
import Button from 'components/Button'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { RootActionType, RootState } from 'duck'
import { ReduxProps } from 'utils/Types'
import { RenderItemProps } from 'components/ListVirtualized'
import FixedSizeList from 'components/ListVirtualized/FixedSizeList'
import GridImage from './GridImage'
import { actions, selectors, STEP } from 'containers/NFTSellPanel/duck'
import { UserImage } from 'models/ApiModels'
import { useBooleanDelayedData, useDelayedRender } from 'utils/Hooks'
import Typography from 'components/Typography'
import { SelectUtils } from 'utils/DataProcessingUtils'
import { GRID_PROJECTS_CONFIG } from '.'
import Dialog from 'components/Dialog'

import stylesGlobal from 'stylesGlobal.module.scss'
import styles from '../../NFTSellForm.module.scss'

const mapStateToProps = (state: RootState) => ({
  gridImages: selectors.gridImages(state),
  gridLoading: selectors.gridLoading(state),
  gridCount: selectors.gridCount(state),
  selectedGridCount: selectors.selectedGridCount(state),
  isSelectedImageEnough: selectors.isSelectedImageEnough(state),
  imageSelectionData: selectors.imageSelectionData(state)
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      toggleSelect: actions.imageSelect.toggleSelect,
      deselectAll: actions.imageSelect.deselectAll,
      selectAll: actions.imageSelect.selectAll,
      setStep: actions.setStep,
      cancel: actions.imageSelect.cancel,
      apply: actions.imageSelect.apply
    },
    dispatch
  )

type DialogImageSelectionProps = ReduxProps<typeof mapStateToProps, typeof mapDispatchToProps> & {
  open: boolean
}

const DialogImageSelection: React.FC<DialogImageSelectionProps> = props => {
  const {
    toggleSelect,
    deselectAll,
    selectAll,
    setStep,
    apply,
    cancel,
    isSelectedImageEnough,
    gridLoading,
    gridImages,
    gridCount,
    selectedGridCount,
    imageSelectionData,
    open
  } = props

  const scrollRef = useRef<HTMLDivElement>(null)
  const shouldRender = useDelayedRender(1)

  return (
    <Dialog
      open={open}
      transition="slideUp"
      classes={{
        paper: 'bg-primary',
        content: 'flex flex-col'
      }}
      hideDialogClose
      fullScreen
      disableContentSpacing
    >
      <div className={clsx('flex justify-between py-2', stylesGlobal.BorderBottomOnSurface)}>
        <Button
          onClick={() => {
            cancel()
            setStep(STEP.SELECT_COVER)
          }}
        >
          Cancel
        </Button>

        <Button
          color="secondary"
          disabled={!isSelectedImageEnough}
          onClick={() => {
            apply()
            setStep(STEP.SELECT_COVER)
          }}
        >
          Done
        </Button>
      </div>

      <div className={clsx('py-2', stylesGlobal.BorderBottomOnSurface)}>
        <Button
          className="mr-1"
          disabled={selectedGridCount === gridCount}
          onClick={() => selectAll()}
        >
          Select All
        </Button>

        <Button disabled={selectedGridCount === 0} onClick={() => deselectAll()}>
          Deselect All
        </Button>
      </div>

      <div ref={scrollRef} className="h-1/100 min-h-0 flex-auto overflow-auto">
        <div className={clsx('w-full py-6 text-white-med-on-dark', styles.ContainerImagesList)}>
          <Typography variant="body2" className="mb-3">
            {`${selectedGridCount} / ${gridCount} images will be in your collection`}
          </Typography>

          <Typography variant="body2">10 images must be selected minimum</Typography>
        </div>

        {shouldRender ? (
          <FixedSizeList<UserImage>
            autoHeight
            scrollRef={scrollRef}
            dataLength={gridImages.length}
            loading={Boolean(gridLoading)}
            config={GRID_PROJECTS_CONFIG}
            renderItem={(props: RenderItemProps<UserImage>) => {
              const { height, index, width } = props

              if (!gridImages[index]) return null

              const item = gridImages[index]

              const selected = SelectUtils.isSelected(
                item.id,
                imageSelectionData.selected,
                imageSelectionData.direction
              )

              return (
                <div className="relative h-full w-full select-none">
                  <ButtonBase
                    disabled={item.is_published}
                    className="h-full w-full"
                    onClick={() => toggleSelect(item.id)}
                  >
                    <GridImage
                      component="span"
                      containerWidth={width}
                      containerHeight={height}
                      selected={selected}
                      image={item}
                    />
                  </ButtonBase>

                  <div
                    className={clsx(
                      'pointer-events-none absolute left-2 top-2 z-1 hidden',
                      selected ? 'flex' : null
                    )}
                  >
                    <CheckCircleIcon color="secondary" />
                  </div>
                </div>
              )
            }}
          />
        ) : null}
      </div>
    </Dialog>
  )
}

const DialogImageSelectionConnected = connect(
  mapStateToProps,
  mapDispatchToProps
)(DialogImageSelection)

const DialogImageSelectionContainer: React.FC<{ open: boolean }> = props => {
  const open = useBooleanDelayedData(props.open)

  if (!open) return null

  return <DialogImageSelectionConnected open={open} />
}

export default DialogImageSelectionContainer
